import React, { useCallback } from "react";
import propTypes from "prop-types";
import { useFormContext } from "./Form";
import useField, { fieldPropTypes } from "./field";

import toastr from "toastr";
import { useDropzone } from "react-dropzone";

const DZStyle = {
    border: "2px dashed #d6242d",
    borderRadius: "20px",
    minHeight: "200px",
    width: "100%",
    display: "flex",
    color: "#d6242d",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    fontSize: "20px"
};

const getPreview = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = ({target: {result}}) => resolve(result);
    reader.onerror = () => reject(new Error(`Could not generate preview for ${file.name}.`));

    reader.readAsDataURL(file);
});

const ImageDrop = ({
    onDrop,
    children,
    ...props
}) => {
    const context = useFormContext();
    const {
        key,
        name,
        errors,
        value,
        label,
        handleValidation,
        fieldProps
    } = useField(props, context, [
        "value",
        "data-label",
        "name",
        "required",
        "onChange",
        "onInvalid",
    ]);

    const files = value || context.getValue(name, []);

    const handleDrop = useCallback(function (newFiles) {
        if (newFiles.length === 0) {
            toastr.error("Oops. Something is wrong with the file you have chosen. Please ensure the image is the correct file type and does not exceed the max allowed file size.");
            return;
        }

        const loadPreviews = newFiles.map((file) => getPreview(file)
            .then((dataUrl) => {
                file.preview = dataUrl;
                return file;
            })
            .catch((err) => {
                toastr.error(err.message);
                return file;
            })
        );

        Promise.all(loadPreviews).then((filesWithPreviews) => {
            const updatedFiles = files.concat(filesWithPreviews);

            context.setValue("files", updatedFiles);
            if (onDrop) {
                onDrop(updatedFiles);
            }
        });
    }, [context, files, onDrop]);

    const {getRootProps, getInputProps} = useDropzone({onDrop: handleDrop});

    return [
        <div
            key={key}
            {...getRootProps({
                style: DZStyle,
                ...fieldProps,
            })}
        >
            <input {...getInputProps({
                name: name,
                "data-label": label,
                onInvalid: handleValidation,
            })} />
            {children}
        </div>,
        ...errors.elements,
    ];
};

ImageDrop.propTypes = {
    ...fieldPropTypes,
    children: propTypes.any,
};

export default ImageDrop;
